"use client"

import DashboardContentWrapper from '../../../../shared/components/dashboard-content-wrapper'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../shared/components/ui/accordion'
import { TypographyH4, TypographyLarge, TypographySmall } from '../../../../shared/components/ui/typography'
import Heading from '../../../../shared/components/ui/typography/heading'
import ReportPieChart from '@/features/reports/components/report-pie-chart'
import WorkscopeStatusBarChart, { GoodwillWorkscopeCompletionPieChart } from '@/features/reports/components/workscope-status-bar-chart'
import ClaimedByWorkscopeStatus from '@/features/reports/components/claimed-by-workscope-status'
import { ReportsPhotosCarousel } from '@/features/reports/components/reports-photos-carousel'
import { WorkOrderStatusRadialChart } from '@/features/reports/components/work-order-status-radial-chart'
import OverallStatusBarChart from '@/features/reports/components/overall-status-bar-chart'
import { useFilterSearchHomes, FilterSearchHomesResponse } from '@/features/homeowners/hooks/filter-search-homes.hook'
import { cn } from '../../../../shared/lib/utils'
import dynamic from 'next/dynamic'
import { useRegionId } from '../../../../shared/hooks/use-region-id'
import LocationSelector from '../../../../shared/components/location-selector'
import ScrollToTopFab from '@/shared/components/scroll-to-top-fab'

// Dynamically import PDF viewers to avoid SSR issues
const BetterTogetherReportViewer = dynamic(
    () => import('@/features/reports/components/better-together-report-viewer'),
    { 
        ssr: false,
        loading: () => (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-2">Loading Better Together Report...</span>
            </div>
        )
    }
)

const ImpactReportViewer = dynamic(
    () => import('@/features/reports/components/impact-report-flipbook-images'),
    { 
        ssr: false,
        loading: () => (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-2">Loading Impact Report viewer...</span>
            </div>
        )
    }
)
import { SquareCheck, User2, Edit2, Check, X, Clock, CalendarPlus, MessageSquare } from 'lucide-react'
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { PartnerRegionAssociation } from '@/features/partners/types/partner.type'
import { BaseLayoutConfig } from '@/features/base/types/base-layout'
import { resolveBaseLayoutConfig } from '@/features/base/utils/layout'
import { unwrapBaseRegionResponse } from '@/features/base/types/region-response'
import { sanitizeHtmlContent } from '@/features/base/utils/sanitize'
import axios from "axios";
import { Input } from '../../../../shared/components/ui/input';
import { useSession } from '../../../../features/auth/context/session.context';
import { UserRole } from '../../../../shared/constants/user-roles';
import PrintButton from '@/features/work-orders/components/print-button';
import { toast } from 'sonner';
import type {
    CurrentStatusData,
    PermitStatusData,
    RebuildStats,
    ReliefStats,
    VolunteerStats,
    WorkScopeStats,
    WorkscopeStats,
} from '@/features/reports/types';

const REPORTS_ACCORDION_HEADINGS = {
    overallCurrentStatus: 'Overall Status',
    workScopeStatus: 'Workscope Status',
    claimedByWorkscopeStatus: 'Claimed By Status',
    photos: 'Job Board',
    permitAndInspectionStatus: 'Permit & Inspection Status',
    partnerReport: 'Partner Give List',
    workOrderStatus: 'Work Order Status',
    betterTogetherReport: 'Better Together Impact Report',
    impactReport: 'Adventures Relief Impact Report',
}

function extractAssociations(raw: unknown): PartnerRegionAssociation[] {
    if (Array.isArray(raw)) {
        return raw as PartnerRegionAssociation[]
    }
    if (raw && typeof raw === 'object') {
        const obj = raw as Record<string, unknown>
        if (obj.data !== undefined) {
            return extractAssociations(obj.data)
        }
    }
    return []
}

function hasRelevantRrgWork(statuses: Array<string | null | undefined>): boolean {
    return statuses.some((status) => status && status !== 'No Assistance Required')
}

function isRrgWorkComplete(statuses: Array<string | null | undefined>): boolean {
    const relevantStatuses = statuses.filter((status) => status && status !== 'No Assistance Required')
    return relevantStatuses.length > 0 && relevantStatuses.every((status) => status === 'Complete')
}

function isRecoveryComplete(reliefStatus: string | null | undefined, rebuildingStatus: string | null | undefined): boolean {
    return isRrgWorkComplete([reliefStatus, rebuildingStatus])
}

function isGoodwillOnlyComplete(
    reliefStatus: string | null | undefined,
    rebuildingStatus: string | null | undefined,
    goodwillStatus: string | null | undefined
): boolean {
    return !hasRelevantRrgWork([reliefStatus, rebuildingStatus]) && goodwillStatus === 'Complete'
}

function ReportsPage() {
    const { session } = useSession();
    const { regionId, region, isLoading: regionLoading, regionSlug } = useRegionId();
    /** Better Together Impact Report is only for NC Swannanoa Valley (`/swannanoa-valley/...`). */
    const showBetterTogetherImpactReport =
        regionSlug?.toLowerCase() === 'swannanoa-valley'
    const shouldFetchHomes = !regionLoading && regionId != null
    const { data: allHomesData } = useFilterSearchHomes(
        shouldFetchHomes ? regionId ?? undefined : undefined,
        true,
        { enabled: shouldFetchHomes }
    )
    const [partners, setPartners] = useState<PartnerRegionAssociation[]>([])
    const [loadingPartners, setLoadingPartners] = useState(false)
    const [partnerGiveListDescription, setPartnerGiveListDescription] = useState<string>('')
    const [statusData, setStatusData] = useState<CurrentStatusData | null>(null);
    const [volunteerData, setVolunteerData] = useState<VolunteerStats | null>(null);
    const [workScopeStats, setWorkScopeStats] = useState<WorkScopeStats | null>(null);
    const [rebuildStats, setRebuildStats] = useState<RebuildStats | null>(null);
    const [reliefStats, setReliefStats] = useState<ReliefStats | null>(null);
    const [workscopeStats, setWorkscopeStats] = useState<WorkscopeStats | null>(null);
    const [priorityData, setPriorityData] = useState<any[]>([]);
    const [volunteersToDate, setVolunteersToDate] = useState<string>('0');
    const [leadersToDate, setLeadersToDate] = useState<string>('0');
    const [isEditingVolunteers, setIsEditingVolunteers] = useState(false);
    const [editVolunteerHours, setEditVolunteerHours] = useState<string>('0');
    const [saving, setSaving] = useState(false);
    const [rollupLoading, setRollupLoading] = useState(false);
    const [fullRegionData, setFullRegionData] = useState<any>(null);
    const [permitData, setPermitData] = useState<PermitStatusData | null>(null);
    const [loadingPermitStatus, setLoadingPermitStatus] = useState(false);

    const isAdmin = session?.role === UserRole.ADMIN;

    /** Homes with any workscope in "Materials Only Complete" (often more accurate than manual OHS). */
    const materialsOnlyCompleteWorkscopeHomeIds = useMemo(() => {
        if (!allHomesData) {
            return new Set<number>()
        }
        const response = allHomesData as FilterSearchHomesResponse
        if (!response.success || !Array.isArray(response.data)) {
            return new Set<number>()
        }

        const activeHomes = response.data.filter((home) => home.isReported === true)
        const materialsOnlyCompleteHomeIds = new Set<number>()

        activeHomes.forEach((home) => {
            const workscopeStatuses = [
                home.treeDebrisInfo?.treeWorkNeeded,
                home.treeDebrisInfo?.exteriorDebrisRemovalNeeded,
                home.treeDebrisInfo?.heavyEquipmentNeeded,
                home.roofingInfo?.roofing,
                home.muckGutInfo?.muckGutNeeded,
                home.moldRemediationInfo?.moldRemediation,
                home.rebuildPrepInfo?.rebuildPrep,
                home.roughCarpentryInfo?.roughCarpentry,
                home.electricalInfo?.electrical,
                home.electricalInfo?.electricalRoughIn,
                home.hvacInfo?.hvac,
                home.hvacInfo?.mechanicalRoughIn,
                home.plumbingInfo?.plumbing,
                home.plumbingInfo?.plumbingRoughIn,
                home.bathroomRebuildInfo?.bathroomRebuild,
                home.crawlSpaceInfo?.crawlSpaceMuck,
                home.crawlSpaceInfo?.crawlSpaceInsulation,
                home.insulationInstallInfo?.insulationInstall,
                home.drywallHangingInfo?.drywallHanging,
                home.mudAndTapeInfo?.mudAndTape,
                home.cabinetInstallInfo?.cabinetInstall,
                home.cabinetInstallInfo?.counterInstall,
                home.interiorPaintInfo?.interiorPaint,
                home.installInteriorDoorsInfo?.installInteriorDoors,
                home.trimInstallInfo?.trimInstall,
                home.flooringInstallInfo?.flooringInstall,
                home.exteriorDoorInstallInfo?.exteriorDoorInstall,
                home.miscellaneousInfo?.miscellaneous,
                home.miscellaneousInfo?.shutterInstall,
                home.miscellaneousInfo?.drivewayRepair,
            ]

            if (workscopeStatuses.some((status) => status === 'Materials Only Complete')) {
                materialsOnlyCompleteHomeIds.add(home.id)
            }
        })

        return materialsOnlyCompleteHomeIds
    }, [allHomesData])

    useEffect(() => {
        const fetchWorkScopeStats = async () => {
            if (!regionId) {
                setPriorityData([])
                setWorkScopeStats(null)
                return
            }
            try {
                const response = await fetch(`/api/priority?regionId=${encodeURIComponent(regionId)}`)
                if (response.ok) {
                    const data = await response.json()
                    setPriorityData(data)
                    
                    // Calculate total jobs (sum of all totalNeeded) and completed jobs (sum of all completed)
                    const totalJobs = data.reduce((sum: number, ws: any) => sum + (ws.totalNeeded || 0), 0)
                    const completedJobs = data.reduce((sum: number, ws: any) => sum + (ws.completed || 0), 0)
                    const percentage = totalJobs > 0 
                        ? Math.round((completedJobs / totalJobs) * 100) 
                        : 0
                    
                    setWorkScopeStats({
                        totalJobs,
                        completedJobs,
                        percentage
                    })
                }
            } catch (error) {
                console.error('Error fetching work scope stats:', error)
            }
        }

        fetchWorkScopeStats()
    }, [regionId])

    // Fetch full region data to get Partner Give List description and volunteerHours
    useEffect(() => {
        const fetchRegionData = async () => {
            if (!regionId) {
                setPartnerGiveListDescription('')
                setVolunteersToDate('0')
                setEditVolunteerHours('0')
                setFullRegionData(null)
                return
            }
            try {
                const response = await fetch(`/api/regions/${regionId}`)
                if (!response.ok) {
                    throw new Error('Failed to load region data')
                }
                const payload = await response.json()
                const region = unwrapBaseRegionResponse(payload)
                
                // Store full region data for use throughout the component
                setFullRegionData(region)
                
                // Extract volunteerHours from full region data
                const volunteerHours = region?.volunteerHours?.toString() || '0';
                setVolunteersToDate(volunteerHours);
                setEditVolunteerHours(volunteerHours);
                
                // Extract Partner Give List description from baseLayoutConfig
                if (region?.baseLayoutConfig) {
                    const layoutConfig = resolveBaseLayoutConfig(region.baseLayoutConfig)
                    // Find Partner Give List section in all tabs
                    let description = ''
                    for (const tab of layoutConfig.tabs) {
                        const partnerGiveListSection = tab.sections.find(s => s.type === 'partnerGiveList')
                        if (partnerGiveListSection?.content) {
                            description = partnerGiveListSection.content
                            break
                        }
                    }
                    setPartnerGiveListDescription(description)
                }
            } catch (error) {
                console.error('Failed to load region data for Partner Give List description:', error)
                setPartnerGiveListDescription('')
                setVolunteersToDate('0')
                setEditVolunteerHours('0')
            }
        }

        fetchRegionData()
    }, [regionId])

    // Fetch partners for Partner Give List
    useEffect(() => {
        const fetchPartners = async () => {
            if (!regionId) {
                setPartners([])
                return
            }
            try {
                setLoadingPartners(true)
                const response = await fetch(`/api/organization-regions?regionId=${regionId}`)
                if (!response.ok) {
                    throw new Error('Failed to load partners')
                }
                const raw = await response.json().catch(() => null)
                
                if (response.ok && raw) {
                    const associations = extractAssociations(raw)
                    setPartners(associations)
                } else {
                    setPartners([])
                }
            } catch (error) {
                console.error('Failed to load partners', error)
                setPartners([])
            } finally {
                setLoadingPartners(false)
            }
        }

        fetchPartners()
    }, [regionId])


    const getCurrentStatusByRegion = async (regionId?: number) => {
        try {
            // Only include regionId if it's a valid number
            const url = regionId !== undefined && regionId !== null && !isNaN(regionId)
                ? `/api/reports/current-status?regionId=${encodeURIComponent(regionId)}`
                : `/api/reports/current-status`;
            const response = await axios.get(url);
            return response.data;
        } catch (error: any) {
            console.error("Error fetching current status:", {
                url: error?.config?.url,
                message: error?.message,
                response: error?.response?.data,
                status: error?.response?.status,
            });
            throw error;
        }
    };

    const getTotalVolunteerByRegion = async (regionId?: number) => {
        try {
            // Only include regionId if it's a valid number
            const url = regionId !== undefined && regionId !== null && !isNaN(regionId)
                ? `/api/reports/volunteers?regionId=${encodeURIComponent(regionId)}`
                : `/api/reports/volunteers`;
            const response = await axios.get(url);
            return response.data.data.data;
        } catch (error: any) {
            console.error("Error fetching volunteers:", {
                url: error?.config?.url,
                message: error?.message,
                response: error?.response?.data,
                status: error?.response?.status,
            });
            throw error;
        }
    };

    // Fetch data when regionId is available
    useEffect(() => {
        if (regionLoading) return; // Wait for region to load
        
        // Don't fetch if regionId is not available yet
        if (!regionId) {
            return
        }

        // get current status - only pass regionId if it's a valid number
        getCurrentStatusByRegion(regionId)
            .then((res) => {
                setStatusData(res.data.data);
            })
            .catch((err) => {
                console.error("Error fetching current status:", err);
            });

        // get total volunteer
        getTotalVolunteerByRegion(regionId)
            .then((data) => {
                setVolunteerData(data); 
            })
            .catch((err) => console.error("Error fetching volunteers:", err));
    }, [regionId, regionLoading]);

    // Fetch permit & inspection status (admin-only section)
    useEffect(() => {
        if (!regionId || !isAdmin) {
            setPermitData(null);
            return;
        }
        let cancelled = false;
        setLoadingPermitStatus(true);
        fetch(`/api/reports/permit-status?regionId=${encodeURIComponent(regionId)}`)
            .then((res) => res.json())
            .then((j) => {
                if (cancelled) return;
                const inner = j?.data?.data;
                setPermitData(inner && typeof inner === 'object' ? inner : null);
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error('Error fetching permit status:', err);
                    setPermitData(null);
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingPermitStatus(false);
            });
        return () => { cancelled = true; };
    }, [regionId, isAdmin]);
    
    // Calculate workscope stats when priority data and status data are available
    useEffect(() => {
        if (priorityData.length > 0 && statusData?.totalHomes) {
            // Collect unique home IDs from ALL work scopes
            const uniqueHomeIds = new Set<number>()
            priorityData.forEach((ws: any) => {
                // Add home IDs from ready homes
                ws.priorities?.ready?.forEach((home: any) => {
                    if (home.homeId) uniqueHomeIds.add(home.homeId)
                })
                // Add home IDs from not ready homes
                ws.priorities?.notReady?.forEach((home: any) => {
                    if (home.homeId) uniqueHomeIds.add(home.homeId)
                })
            })
            
            const homesRequiringWorkscope = uniqueHomeIds.size
            const totalHomes = statusData.totalHomes
            const workscopePercentage = totalHomes > 0 
                ? Math.round((homesRequiringWorkscope / totalHomes) * 100)
                : 0
            
            setWorkscopeStats({
                homesRequiringWorkscope,
                totalHomes,
                percentage: workscopePercentage
            })
        }
    }, [priorityData, statusData])

    // Calculate relief stats when priority data and status data are available
    useEffect(() => {
        if (priorityData.length > 0 && statusData?.totalHomes) {
            // Define relief work scopes
            const reliefWorkScopes = [
                'assess-needs',
                'heavy-equipment-demo',
                'tree-work',
                'exterior-debris-removal',
                'tarp-roof',
                'm-and-g-crawl-space',
                'muck-and-gut-interior',
                'mold-remediation',
                'insulate-crawl-space'
            ]
            
            // Collect unique home IDs from relief work scopes
            const uniqueHomeIds = new Set<number>()
            priorityData.forEach((ws: any) => {
                if (reliefWorkScopes.includes(ws.workScope)) {
                    // Add home IDs from ready homes
                    ws.priorities?.ready?.forEach((home: any) => {
                        if (home.homeId) uniqueHomeIds.add(home.homeId)
                    })
                    // Add home IDs from not ready homes
                    ws.priorities?.notReady?.forEach((home: any) => {
                        if (home.homeId) uniqueHomeIds.add(home.homeId)
                    })
                }
            })
            
            const homesRequiringRelief = uniqueHomeIds.size
            const totalHomes = statusData.totalHomes
            const reliefPercentage = totalHomes > 0 
                ? Math.round((homesRequiringRelief / totalHomes) * 100)
                : 0
            
            setReliefStats({
                homesRequiringRelief,
                totalHomes,
                percentage: reliefPercentage
            })
        }
    }, [priorityData, statusData])

    // Calculate rebuild stats when priority data and status data are available
    useEffect(() => {
        if (priorityData.length > 0 && statusData?.totalHomes) {
            // Blacklist of non-rebuild work scopes to exclude
            // (priorityData already filtered by priorityTable = true from backend)
            const blacklistedScopes = [
                'heavy-equipment-demo',
                'tree-work',
                'exterior-debris-removal',
                'tarp-roof',
                'm-and-g-crawl-space',
                'muck-and-gut-interior',
                'mold-remediation',
                'insulate-crawl-space',
                'misc'
            ]
            
            // Collect unique home IDs from rebuild work scopes (excluding blacklisted)
            const uniqueHomeIds = new Set<number>()
            priorityData.forEach((ws: any) => {
                // Skip blacklisted work scopes
                if (blacklistedScopes.includes(ws.workScope)) {
                    return
                }
                
                // Add home IDs from ready homes
                ws.priorities?.ready?.forEach((home: any) => {
                    if (home.homeId) uniqueHomeIds.add(home.homeId)
                })
                // Add home IDs from not ready homes
                ws.priorities?.notReady?.forEach((home: any) => {
                    if (home.homeId) uniqueHomeIds.add(home.homeId)
                })
            })
            
            const homesRequiringRebuild = uniqueHomeIds.size
            const totalHomes = statusData.totalHomes
            const rebuildPercentage = totalHomes > 0 
                ? Math.round((homesRequiringRebuild / totalHomes) * 100)
                : 0
            
            setRebuildStats({
                homesRequiringRebuild,
                totalHomes,
                percentage: rebuildPercentage
            })
        }
    }, [priorityData, statusData])
    
    const handleEditClick = () => {
        setIsEditingVolunteers(true);
        setEditVolunteerHours(volunteersToDate);
    };

    const handleCancel = () => {
        setIsEditingVolunteers(false);
        setEditVolunteerHours(volunteersToDate);
    };

    const handleSave = async () => {
        if (!regionId) return;

        setSaving(true);
        try {
            const response = await fetch(`/api/regions/${regionId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    volunteerHours: editVolunteerHours ? parseInt(editVolunteerHours, 10) : 0,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                const updatedRegion = data.success ? data.data : data;
                if (updatedRegion) {
                    const volunteerHours = updatedRegion.volunteerHours?.toString() || '0';
                    setVolunteersToDate(volunteerHours);
                    setEditVolunteerHours(volunteerHours);
                    setIsEditingVolunteers(false);
                }
            } else {
                console.error('Failed to update volunteer hours');
            }
        } catch (error) {
            console.error('Error updating volunteer hours:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleRollupLastWeek = async () => {
        if (!regionId) return;
        setRollupLoading(true);
        try {
            const response = await fetch(
                `/api/stm/weeks/rollup-volunteers-to-date?regionId=${encodeURIComponent(regionId)}`,
                { method: 'POST', credentials: 'include' }
            );
            const data = await response.json().catch(() => ({}));
            if (response.ok && data.volunteersToDate != null) {
                const next = String(data.volunteersToDate);
                setVolunteersToDate(next);
                setEditVolunteerHours(next);
                setIsEditingVolunteers(false);
                const added = data.previousWeekCount ?? 0;
                toast.success(
                    added > 0
                        ? `Added ${added} volunteers from last week. Volunteers To Date: ${next}`
                        : 'Volunteers To Date updated (no volunteers on base last week).'
                );
            } else {
                const msg = data?.message || data?.error || `Request failed (${response.status})`;
                toast.error(msg);
                console.error('Rollup failed:', msg);
            }
        } catch (error) {
            console.error('Error rolling up volunteers to date:', error);
        } finally {
            setRollupLoading(false);
        }
    };

    // Calculate Est Volunteer Hours to Date: (Volunteers * 54 hours)
    const calculateVolunteerHours = () => {
        const volunteers = parseInt(volunteersToDate, 10) || 0;
        const totalHours = volunteers * 54;
        return new Intl.NumberFormat('en-US').format(totalHours);
    };

    // Calculate Est FEMA Volunteer Labor: (Volunteers * 54 hours * $33.01/hour)
    const calculateFemaLabor = () => {
        const volunteers = parseInt(volunteersToDate, 10) || 0;
        const totalHours = volunteers * 54;
        const laborCost = totalHours * 33.01;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(laborCost);
    };
    
    return (
        <DashboardContentWrapper className='space-y-5'>
            <div className="flex flex-col gap-4 md:flex-row md:justify-between">
                <div className="flex items-center gap-2">
                    <Heading>Reports</Heading>
                    {(isAdmin || session?.role === UserRole.BASE_LEADER) && (
                        <PrintButton title="Print Reports" />
                    )}
                </div>
                <LocationSelector />
            </div>
            <Accordion
                type="multiple"
                className='space-y-6'
                defaultValue={[
                    REPORTS_ACCORDION_HEADINGS.overallCurrentStatus,
                    REPORTS_ACCORDION_HEADINGS.workScopeStatus,
                    REPORTS_ACCORDION_HEADINGS.claimedByWorkscopeStatus,
                    REPORTS_ACCORDION_HEADINGS.photos
                ]}
            >
                <ReportCard heading={REPORTS_ACCORDION_HEADINGS.overallCurrentStatus}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                        <ReportSecondaryCard
                            title='Total Homeowners Supported'
                            value={statusData?.totalHomes?.toString() ?? "0"}
                            icon={<SquareCheck />}
                        />
                        <div className={cn('bg-background rounded p-4 w-full flex justify-between items-center gap-5 border')}>
                            <div className="flex-1">
                                <p className='text-muted-foreground text-sm'>On Base Volunteers To Date</p>
                                {regionLoading ? (
                                    <div className="mt-1">
                                        <p className='text-lg font-semibold text-muted-foreground'>Loading...</p>
                                    </div>
                                ) : isEditingVolunteers ? (
                                    <div className="flex items-center gap-2 mt-1">
                                        <Input
                                            type="text"
                                            value={editVolunteerHours}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditVolunteerHours(e.target.value)}
                                            className="h-8 max-w-[150px]"
                                            autoFocus
                                            disabled={saving}
                                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                if (e.key === 'Enter' && !saving) handleSave();
                                                if (e.key === 'Escape' && !saving) handleCancel();
                                            }}
                                        />
                                        <button onClick={handleSave} disabled={saving} className="text-green-600 hover:text-green-700 disabled:opacity-50">
                                            <Check className="w-4 h-4" />
                                        </button>
                                        <button onClick={handleCancel} disabled={saving} className="text-red-600 hover:text-red-700 disabled:opacity-50">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <p className='text-lg font-semibold'>{volunteersToDate}</p>
                                        {isAdmin && (
                                            <>
                                                <button
                                                    onClick={handleRollupLastWeek}
                                                    disabled={rollupLoading}
                                                    className="text-muted-foreground hover:text-foreground disabled:opacity-50 flex items-center gap-1"
                                                    title="Add last week's volunteers on base to Volunteers To Date (run on Sunday)"
                                                >
                                                    <CalendarPlus className="w-4 h-4" />
                                                    <span className="text-xs hidden sm:inline">Add last week</span>
                                                </button>
                                                <button onClick={handleEditClick} className="text-muted-foreground hover:text-foreground">
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className='bg-primary/10 rounded-full p-3 w-12 aspect-square text-primary'>
                                <User2 />
                            </div>
                        </div>
                        <ReportSecondaryCard
                            title='Est Volunteer Hours to Date'
                            value={calculateVolunteerHours()}
                            icon={<Clock />}
                        />
                        <ReportSecondaryCard
                            title='Est FEMA Volunteer Labor'
                            value={calculateFemaLabor()}
                            icon={<User2 />}
                        />
                        <ReportSecondaryCard
                            title='Current Week Homeowner Touchpoints'
                            value={statusData?.currentWeekHomeownerTouchpoints?.toString() ?? '0'}
                            icon={<MessageSquare />}
                        />
                    </div>
                    <div className="pt-5">
                        <OverallStatusBarChart totalHomesSupported={statusData?.totalHomes} />
                    </div>
                    <div className="grid grid-cols-1 gap-3 pt-5 md:grid-cols-2 xl:grid-cols-3 print:grid-cols-2 print:break-inside-avoid">
                        <ReportPieChart 
                            title='Relief & Rebuild Workscopes Status' 
                            numerator={workScopeStats?.completedJobs ?? 0} 
                            denominator={workScopeStats?.totalJobs ?? 0} 
                            percentage={workScopeStats?.percentage ?? 0}
                            chartData={[
                                { key: 'incomplete', value: (workScopeStats?.totalJobs ?? 0) - (workScopeStats?.completedJobs ?? 0), fill: "#C9A961" },
                                { key: 'completed', value: workScopeStats?.completedJobs ?? 0, fill: "#3E7C59" },
                            ]}
                            chartConfig={{
                                incomplete: {
                                    label: "To Go",
                                    color: "#C9A961",
                                },
                                completed: {
                                    label: "Completed",
                                    color: "#3E7C59",
                                },
                            }}
                        />
                        <GoodwillWorkscopeCompletionPieChart
                            priorityData={priorityData}
                        />
                        {(() => {
                            const response = allHomesData as FilterSearchHomesResponse | undefined
                            const activeHomes = response?.success && Array.isArray(response.data)
                                ? response.data.filter((home) => home.isReported === true)
                                : []
                            const homesWithRrgWork = activeHomes.filter((home) =>
                                hasRelevantRrgWork([home.reliefStatus, home.rebuildingStatus, home.goodwillStatus])
                            )

                            let functionallyCompleteOnly = 0
                            let fullyComplete = 0
                            let goodwillHomeComplete = 0
                            let materialsOnlyComplete = 0
                            let notComplete = 0

                            homesWithRrgWork.forEach((home) => {
                                if (home.homeStatusInfo?.overallStatus === 'Functionally Complete') {
                                    functionallyCompleteOnly++
                                    return
                                }

                                if (isRecoveryComplete(home.reliefStatus, home.rebuildingStatus)) {
                                    if (materialsOnlyCompleteWorkscopeHomeIds.has(home.id)) {
                                        materialsOnlyComplete++
                                    } else {
                                        fullyComplete++
                                    }
                                    return
                                }

                                if (isGoodwillOnlyComplete(home.reliefStatus, home.rebuildingStatus, home.goodwillStatus)) {
                                    if (materialsOnlyCompleteWorkscopeHomeIds.has(home.id)) {
                                        materialsOnlyComplete++
                                    } else {
                                        goodwillHomeComplete++
                                    }
                                    return
                                }

                                notComplete++
                            })

                            const totalHomesWithWorkscope = homesWithRrgWork.length
                            const totalCompleted =
                                fullyComplete +
                                functionallyCompleteOnly +
                                goodwillHomeComplete +
                                materialsOnlyComplete
                            
                            return (
                                <ReportPieChart
                                    title='Homes Completion Status'
                                    numerator={totalCompleted}
                                    denominator={totalHomesWithWorkscope}
                                    percentage={
                                        totalHomesWithWorkscope > 0
                                            ? Math.round((totalCompleted / totalHomesWithWorkscope) * 100)
                                            : 0
                                    }
                                    chartData={[
                                        { key: 'fullyComplete', value: fullyComplete, fill: "#3E7C59" },
                                        { key: 'functionallyCompleteOnly', value: functionallyCompleteOnly, fill: "#5BA375" },
                                        { key: 'goodwillHomeComplete', value: goodwillHomeComplete, fill: "#2E8B57" },
                                        { key: 'materialsOnlyComplete', value: materialsOnlyComplete, fill: "#7CB342" },
                                        { key: 'notComplete', value: notComplete, fill: "#C9A961" },
                                    ]}
                                    chartConfig={{
                                        fullyComplete: {
                                            label: "Complete",
                                            color: "#3E7C59",
                                        },
                                        functionallyCompleteOnly: {
                                            label: "Functionally Complete",
                                            color: "#5BA375",
                                        },
                                        goodwillHomeComplete: {
                                            label: "Goodwill Home Complete",
                                            color: "#2E8B57",
                                        },
                                        materialsOnlyComplete: {
                                            label: "Materials Only Complete",
                                            color: "#7CB342",
                                        },
                                        notComplete: {
                                            label: "Not Complete",
                                            color: "#C9A961",
                                        },
                                    }}
                                />
                            )
                        })()}
                        {/* Temporarily hidden - may not want these charts
                        <ReportPieChart
                            title='Homes Currently Requiring Relief'
                            numerator={reliefStats?.homesRequiringRelief ?? 0}
                            denominator={reliefStats?.totalHomes ?? 0}
                            percentage={reliefStats?.percentage ?? 0}
                            chartData={[
                                { key: 'reliefRequired', value: reliefStats?.homesRequiringRelief ?? 0, fill: "#D4A017" },
                                { key: 'noReliefRequired', value: (reliefStats?.totalHomes ?? 0) - (reliefStats?.homesRequiringRelief ?? 0), fill: "#3E7C59" },
                            ]}
                            chartConfig={{
                                reliefRequired: {
                                    label: "Relief",
                                    color: "orange",
                                },
                                noReliefRequired: {
                                    label: "None",
                                    color: "blue",
                                },
                            }}
                        />
                        <ReportPieChart
                            title='Homes Currently Requiring Rebuild'
                            numerator={rebuildStats?.homesRequiringRebuild ?? 0}
                            denominator={rebuildStats?.totalHomes ?? 0}
                            percentage={rebuildStats?.percentage ?? 0}
                            chartData={[
                                { key: 'rebuildRequired', value: rebuildStats?.homesRequiringRebuild ?? 0, fill: "#D4A017" },
                                { key: 'noRebuildRequired', value: (rebuildStats?.totalHomes ?? 0) - (rebuildStats?.homesRequiringRebuild ?? 0), fill: "#3E7C59" },
                            ]}
                            chartConfig={{
                                rebuildRequired: {
                                    label: "Rebuild",
                                    color: "green",
                                },
                                noRebuildRequired: {
                                    label: "None",
                                    color: "blue",
                                },
                            }}
                        />
                        */}
                    </div>
                </ReportCard>

                <ReportCard
                    heading={REPORTS_ACCORDION_HEADINGS.workScopeStatus}
                    className="print:break-inside-avoid"
                >
                    <div className="space-y-3">
                        <h3 className="text-center text-lg font-semibold">Relief & Rebuild Workscope Status</h3>
                        <WorkscopeStatusBarChart />
                    </div>
                </ReportCard>

                <ReportCard
                    heading={REPORTS_ACCORDION_HEADINGS.claimedByWorkscopeStatus}
                    className="print:break-inside-avoid"
                >
                    <ClaimedByWorkscopeStatus allHomesData={allHomesData as FilterSearchHomesResponse | undefined} />
                </ReportCard>

                <ReportCard heading={REPORTS_ACCORDION_HEADINGS.photos} className="print:break-inside-avoid print:break-before-page">
                    <ReportsPhotosCarousel
                        regionId={regionId ?? null}
                        componentName={REPORTS_ACCORDION_HEADINGS.workScopeStatus}
                        canEdit={isAdmin || session?.role === UserRole.BASE_LEADER}
                    />
                </ReportCard>

                {/* Better Together Impact Report - NC Swannanoa Valley only; hidden when printing */}
                {showBetterTogetherImpactReport && (
                <ReportCard 
                    heading={REPORTS_ACCORDION_HEADINGS.betterTogetherReport}
                    className="bg-muted print:hidden"
                >
                    <BetterTogetherReportViewer />
                </ReportCard>
                )}

                {/* Adventures Relief Impact Report - Visible to all users, hidden when printing */}
                <ReportCard 
                    heading={REPORTS_ACCORDION_HEADINGS.impactReport}
                    className="bg-muted print:hidden"
                >
                    <ImpactReportViewer />
                </ReportCard>

                <AccordionItem
                    className={cn('bg-muted rounded p-4 border flex flex-col gap-3 print:hidden')}
                    value={REPORTS_ACCORDION_HEADINGS.partnerReport}
                >
                    <AccordionTrigger className='py-0' downChevronIconClassName='w-5 h-5'>
                        <div className="flex flex-col items-start gap-2 w-full">
                            <h2 className="text-xl font-semibold">{REPORTS_ACCORDION_HEADINGS.partnerReport}</h2>
                            {(() => {
                                const safeHtml = sanitizeHtmlContent(partnerGiveListDescription)
                                return safeHtml ? (
                                    <div className="prose max-w-none text-sm text-muted-foreground text-left" dangerouslySetInnerHTML={{ __html: safeHtml }} />
                                ) : null
                            })()}
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className='pb-0'>
                        {loadingPartners ? (
                            <div className="text-center py-8">
                                <div className="text-muted-foreground">Loading partners...</div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {!regionId ? (
                                    <p className="text-sm text-muted-foreground">
                                        Please select a region to view partners with giving links.
                                    </p>
                                ) : partners.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">
                                        No partners with giving links available at this time.
                                    </p>
                                ) : (
                                    <div className="space-y-3">
                                        {partners
                                            .filter((association) => {
                                                const org = association.organization
                                                const hasOrganization = !!org
                                                const giveLink = org?.giveLink
                                                const hasGiveLink = !!giveLink && giveLink.trim().length > 0
                                                return hasOrganization && hasGiveLink
                                            })
                                            .map((association) => {
                                                const partner = association.organization
                                                return (
                                                    <div
                                                        key={association.id}
                                                        className="flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-4"
                                                    >
                                                        <h4 className="font-semibold text-foreground">{partner.name}</h4>
                                                        {partner.giveLink && (
                                                            <div>
                                                                <a
                                                                    href={partner.giveLink}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-primary hover:underline text-sm"
                                                                >
                                                                    {partner.name} Giving Link
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                    </div>
                                )}
                            </div>
                        )}
                    </AccordionContent>
                </AccordionItem>

                {/* Admin-only: Work Order Status — excluded from print until activated */}
                {session?.role === UserRole.ADMIN && (
                    <ReportCard 
                        heading={REPORTS_ACCORDION_HEADINGS.workOrderStatus}
                        className="bg-transparent border-none shadow-none print:hidden"
                        headingClassName="opacity-40"
                    >
                        <WorkOrderStatusRadialChart />
                    </ReportCard>
                )}

                {/* Admin-only: Permit & Inspection Status — excluded from print until activated */}
                {session?.role === UserRole.ADMIN && (
                    <ReportCard 
                        heading={REPORTS_ACCORDION_HEADINGS.permitAndInspectionStatus} 
                        className="bg-transparent border-none shadow-none print:hidden"
                        headingClassName="opacity-40"
                    >
                        {loadingPermitStatus ? (
                            <div className="py-6 text-center text-muted-foreground">Loading permit status…</div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                    <ReportSecondaryCard
                                        title='Total Number Permits'
                                        value={permitData ? String(permitData.totalPermits) : '—'}
                                        icon={<SquareCheck />}
                                    />
                                    <ReportSecondaryCard
                                        title='Closed Permits'
                                        value={permitData ? String(permitData.closedPermits) : '—'}
                                        icon={<SquareCheck />}
                                    />
                                    <ReportSecondaryCard
                                        title='Open Permits'
                                        value={permitData ? String(permitData.openPermits) : '—'}
                                        icon={<SquareCheck />}
                                    />
                                </div>
                                <div className="grid pt-5 grid-cols-[repeat(auto-fit,_minmax(250px,_1fr))] gap-3">
                                    <ReportSecondaryCard
                                        title='Open Partner Home Permits'
                                        value={permitData ? String(permitData.openPartnerHomePermits) : '—'}
                                        icon={<SquareCheck />}
                                    />
                                    <ReportSecondaryCard
                                        title='Homeowner Responsible Permits'
                                        value={permitData ? String(permitData.homeownerResponsiblePermits) : '—'}
                                        icon={<SquareCheck />}
                                    />
                                    <ReportSecondaryCard
                                        title='Open Permits by Work Scope'
                                        value={permitData ? String(permitData.openPermitsByWorkScope) : '—'}
                                        icon={<SquareCheck />}
                                    />
                                    <ReportSecondaryCard
                                        title='Close Permits by Work Scope'
                                        value={permitData ? String(permitData.closedPermitsByWorkScope) : '—'}
                                        icon={<SquareCheck />}
                                    />
                                </div>
                            </>
                        )}
                    </ReportCard>
                )}
            </Accordion>
            <ScrollToTopFab />
        </DashboardContentWrapper>
    )
}



type ReportCardProps = PropsWithChildren & { className?: string, heading: string, headingClassName?: string }

const ReportCard = ({ children, className, heading, headingClassName }: ReportCardProps) => {
    return (
        <AccordionItem
            className={cn('bg-muted rounded p-4 border flex flex-col gap-3', className)}
            value={heading}
        >
            <AccordionTrigger className='py-0' downChevronIconClassName='w-5 h-5'>
                <h2 className={cn('text-xl font-semibold', headingClassName)}>{heading}</h2>
            </AccordionTrigger>
            <AccordionContent className='pb-0'>
                {children}
            </AccordionContent>
        </AccordionItem>
    )
}

const ReportSecondaryCard = (props: {
    title: string
    value: string
    icon: React.ReactNode
    className?: string
}) => {
    return (
        <div className={cn('bg-background rounded p-4 w-full flex justify-between items-center gap-5 border', props.className)}>
            <div>
                <TypographySmall className='text-muted-foreground text-sm'>{props.title}</TypographySmall>
                <TypographyLarge>{props.value}</TypographyLarge>
            </div>
            <div className='bg-primary/10 rounded-full p-3 w-12 aspect-square text-primary'>
                {props.icon}
            </div>
        </div>
    )
}

export default ReportsPage

